import { useEffect, useMemo } from 'react';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import {
  useExistingNotes,
  useExistingStandalonePatientVisitNotes,
} from '@/react-query/videa-notes/useExistingNotes';

interface UseAppointmentNotesProps {
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  isSheetOpen: boolean;
}

interface UseAppointmentNotesReturn {
  clinicalNotesData: ClinicalNote[];
  isLoading: boolean;
  error: Error | null;
  isWritebackInProgress: boolean;
  refetchClinicalNotes: () => Promise<{ data: ClinicalNote[] | undefined }>;
  refetchStandalonePatientVisitNotes: () => Promise<{
    data: ClinicalNote[] | undefined;
  }>;
}

/**
 * Hook to manage fetching clinical notes for both unified appointments
 * and standalone patient visits. Abstracts the dual-source complexity.
 */
export const useAppointmentNotes = ({
  unifiedAppointmentId,
  standalonePatientVisitId,
  isSheetOpen,
}: UseAppointmentNotesProps): UseAppointmentNotesReturn => {
  // Fetch notes for unified appointments
  const {
    data: unifiedAppointmentNotesData = [],
    error: unifiedAppointmentNotesError,
    isLoading: isLoadingClinicalNotes,
    refetch: refetchClinicalNotes,
  } = useExistingNotes(
    unifiedAppointmentId,
    isSheetOpen && !!unifiedAppointmentId,
  );

  // Fetch notes for standalone patient visits
  const {
    data: standalonePatientVisitNotesData = [],
    error: standalonePatientVisitNotesError,
    isLoading: isLoadingStandalonePatientVisitNotes,
    refetch: refetchStandalonePatientVisitNotes,
  } = useExistingStandalonePatientVisitNotes(
    standalonePatientVisitId,
    isSheetOpen && !!standalonePatientVisitId,
  );

  // Combine/select notes from the appropriate source
  const clinicalNotesData = useMemo(() => {
    // Only show notes from the appropriate source based on appointment type
    if (unifiedAppointmentId) {
      // For regular appointments, only show unified appointment notes
      return unifiedAppointmentNotesData;
    } else if (standalonePatientVisitId) {
      // For standalone visits, only show standalone patient visit notes
      return standalonePatientVisitNotesData;
    }
    // If neither, return empty array
    return [];
  }, [
    unifiedAppointmentId,
    standalonePatientVisitId,
    unifiedAppointmentNotesData,
    standalonePatientVisitNotesData,
  ]);

  // Determine loading state based on which type of appointment we have
  const isLoading = useMemo(() => {
    if (unifiedAppointmentId) {
      return isLoadingClinicalNotes;
    }
    if (standalonePatientVisitId) {
      return isLoadingStandalonePatientVisitNotes;
    }
    return false;
  }, [
    unifiedAppointmentId,
    standalonePatientVisitId,
    isLoadingClinicalNotes,
    isLoadingStandalonePatientVisitNotes,
  ]);

  // Combine errors
  const error = useMemo(() => {
    if (unifiedAppointmentId && unifiedAppointmentNotesError) {
      return unifiedAppointmentNotesError as Error;
    }
    if (standalonePatientVisitId && standalonePatientVisitNotesError) {
      return standalonePatientVisitNotesError as Error;
    }
    return null;
  }, [
    unifiedAppointmentId,
    standalonePatientVisitId,
    unifiedAppointmentNotesError,
    standalonePatientVisitNotesError,
  ]);

  // Detect if any writeback is in progress
  const isWritebackInProgress = useMemo(() => {
    return clinicalNotesData.some(
      (note) =>
        note.writebackStatus?.clinicalNoteId &&
        !note.writebackStatus?.processedAt &&
        note.writebackStatus?.errorCode === null,
    );
  }, [clinicalNotesData]);

  // Poll for updates when writeback is in progress
  useEffect(() => {
    if (isWritebackInProgress) {
      const interval = setInterval(() => {
        if (unifiedAppointmentId) {
          refetchClinicalNotes();
        }
        if (standalonePatientVisitId) {
          refetchStandalonePatientVisitNotes();
        }
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [
    isWritebackInProgress,
    unifiedAppointmentId,
    standalonePatientVisitId,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
  ]);

  return {
    clinicalNotesData,
    isLoading,
    error,
    isWritebackInProgress,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
  };
};
